import React, { useState, useEffect } from 'react';
import { getUserTeamNamingDefaults, updateUserTeamNamingDefaults } from '../../DatabaseAPI';
import { Modal, FormField, Input, Checkbox, Button } from '../ui';

/**
 * Backend contract: `Team.name` is `max_length=100` (football/models.py).
 * Enforced on the input so a name can never be typed that the server would
 * truncate or reject.
 *
 * ⚠️ A season league name is DERIVED from this value by appending "'s Campaign"
 * and is itself capped at 100, so a team name near this limit cannot be used
 * verbatim as a league name. That shortening lives in
 * `deriveSeasonLeagueName()` (frontend/src/api/seasonApi.js) — not here, since
 * the team name itself is perfectly legal at the full 100.
 */
const TEAM_NAME_MAX_LENGTH = 100;

/**
 * @param {Object} [leagueInfo] - display context. Two capability flags let a
 *        caller declare what its endpoint can actually honour:
 *
 *        · `payoutKnown: false`  — suppress the "Win up to" row (see below).
 *        · `supportsKnownAs: false` — suppress the Profile Name field and the
 *          Conceal True Name checkbox, and drop them from the preview.
 *
 *        BOTH EXIST FOR THE SAME REASON: this modal is shared by endpoints with
 *        different capabilities, and a form must never collect — let alone
 *        REQUIRE — input the receiving endpoint will silently discard. The
 *        season create/join endpoints accept `team_name` only
 *        (SeasonLeagueCreationService / SeasonLeagueJoinService both do
 *        `Team.objects.create(league=…, owner=…, name=…)`), so for those the
 *        fields are hidden rather than shown-and-thrown-away.
 */
function TeamNameModal({ isOpen, onClose, onSubmit, leagueInfo }) {
    // Weekly endpoints accept known_as/hide_known_as; season ones do not.
    // Defaults to true so every existing caller is unchanged.
    const supportsKnownAs = leagueInfo?.supportsKnownAs !== false;
    const [teamName, setTeamName] = useState('');
    const [knownAs, setKnownAs] = useState('');
    const [hideKnownAs, setHideKnownAs] = useState(false);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [error, setError] = useState('');
    const [isLoadingDefaults, setIsLoadingDefaults] = useState(false);

    const generateRandomName = () => {
        const adjectives = [
            'Mighty', 'Thunder', 'Lightning', 'Steel', 'Iron', 'Golden', 'Silver', 'Crimson',
            'Azure', 'Emerald', 'Phantom', 'Shadow', 'Blazing', 'Frozen', 'Wild', 'Savage',
            'Royal', 'Elite', 'Supreme', 'Ultimate', 'Legendary', 'Epic', 'Mythic', 'Divine'
        ];
        
        const nouns = [
            'Dragons', 'Eagles', 'Lions', 'Tigers', 'Bears', 'Wolves', 'Hawks', 'Falcons',
            'Panthers', 'Stallions', 'Bulls', 'Rams', 'Vikings', 'Warriors', 'Knights', 'Titans',
            'Giants', 'Champions', 'Legends', 'Heroes', 'Guardians', 'Crusaders', 'Raiders', 'Conquerors'
        ];

        const randomAdjective = adjectives[Math.floor(Math.random() * adjectives.length)];
        const randomNoun = nouns[Math.floor(Math.random() * nouns.length)];
        
        const newName = `${randomAdjective} ${randomNoun}`;
        setTeamName(newName);
        // FIXED: Immediately update user defaults when generating random name so it persists
        updateUserDefaults(newName, knownAs, hideKnownAs);
    };

    // Load user defaults when modal opens
    useEffect(() => {
        if (isOpen) {
            loadUserDefaults();
        }
    }, [isOpen]);

    const loadUserDefaults = async () => {
        setIsLoadingDefaults(true);
        try {
            const data = await getUserTeamNamingDefaults();
            // Access the nested 'defaults' object from API response
            const defaults = data.defaults || {};
            
            // FIXED: Always use stored values, only generate random if NO stored team name
            if (defaults.team_name) {
                setTeamName(defaults.team_name);
            } else {
                generateRandomName(); // Only generate if no stored value
            }
            
            if (defaults.known_as) {
                setKnownAs(defaults.known_as);
            }
            // NO FALLBACK - backend must provide email
            
            setHideKnownAs(defaults.hide_known_as || false);
            
        } catch (error) {
            console.error('❌ LOUD ERROR: Failed to load user defaults:', error);
            // NO FALLBACKS - the backend API should always work
            generateRandomName();
        } finally {
            setIsLoadingDefaults(false);
        }
    };

    const updateUserDefaults = async (newTeamName, newKnownAs, newHideKnownAs) => {
        try {
            await updateUserTeamNamingDefaults({
                team_name: newTeamName,
                known_as: newKnownAs,
                hide_known_as: newHideKnownAs
            });
            // Don't show errors for default updates, just log them
        } catch (error) {
            console.warn('Failed to update user defaults:', error);
        }
    };

    const getDisplayName = () => {
        // The preview must match what the SERVER will store. A season team is
        // created with `name` only, so showing "Team (Profile)" there would
        // promise a display name the backend never persists.
        if (supportsKnownAs && knownAs && !hideKnownAs) {
            return `${teamName} (${knownAs})`;
        }
        return teamName;
    };

    const handleTeamNameChange = (newName) => {
        setTeamName(newName);
        updateUserDefaults(newName, knownAs, hideKnownAs);
    };

    const handleKnownAsChange = (newKnownAs) => {
        setKnownAs(newKnownAs);
        updateUserDefaults(teamName, newKnownAs, hideKnownAs);
    };

    const handleHideKnownAsChange = (newHide) => {
        setHideKnownAs(newHide);
        updateUserDefaults(teamName, knownAs, newHide);
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        console.log('🔍 TeamNameModal: handleSubmit called');
        
        if (!teamName.trim()) {
            console.log('❌ TeamNameModal: Team name validation failed');
            setError('Team name is required');
            return;
        }

        // Only required where the endpoint can actually store it. Requiring a
        // field that will be discarded is the worst of both worlds: it blocks
        // submission AND throws the value away.
        if (supportsKnownAs && !knownAs.trim()) {
            console.log('❌ TeamNameModal: Profile name validation failed');
            setError('Profile Name is required');
            return;
        }

        console.log('✅ TeamNameModal: Validation passed, starting submission');
        setIsSubmitting(true);
        setError('');

        try {
            // Pass the complete team naming data to the parent component
            const teamData = {
                name: teamName.trim(),
                known_as: knownAs.trim(),
                hide_known_as: hideKnownAs
            };
            
            console.log('🔍 TeamNameModal: Calling onSubmit with teamData:', teamData);
            console.log('🔍 TeamNameModal: League info:', leagueInfo);
            
            await onSubmit(teamData, leagueInfo);
            console.log('✅ TeamNameModal: onSubmit completed successfully');
            handleClose();
        } catch (err) {
            console.error('❌ TeamNameModal: onSubmit failed with error:', err);
            // Handle profanity errors specifically
            if (err.message && err.message.includes('inappropriate language')) {
                setError('Team name or Profile Name contains inappropriate language. Please choose different names.');
            } else {
                setError(err.message || 'Failed to create team. Please try again.');
            }
        } finally {
            setIsSubmitting(false);
        }
    };

    const handleClose = () => {
        setTeamName('');
        setKnownAs('');
        setHideKnownAs(false);
        setError('');
        onClose();
    };

    if (!isOpen) return null;

    return (
        <Modal 
            isOpen={isOpen} 
            onClose={handleClose}
            title={null}
            size="md"
        >
            {/* Header with Logo */}
            <div style={{
                textAlign: 'center',
                marginBottom: 'var(--space-6)'
            }}>
                <img
                    src="/art/Logo.png"
                    alt="Mythic Football Logo"
                    style={{
                        width: '120px',
                        height: 'auto',
                        marginBottom: 'var(--space-4)'
                    }}
                />
                {leagueInfo && (
                    <div style={{
                        fontSize: '1.125rem',
                        fontWeight: 'bold',
                        color: 'var(--color-text-primary)',
                        marginBottom: 'var(--space-5)'
                    }}>
                        {leagueInfo.name}
                    </div>
                )}
            </div>

            {/* Loading State */}
            {isLoadingDefaults && (
                <div style={{
                    textAlign: 'center',
                    padding: 'var(--space-5)',
                    color: 'var(--color-text-tertiary)'
                }}>
                    Loading your preferences...
                </div>
            )}

            {/* Form Content */}
            {!isLoadingDefaults && (
                <form onSubmit={(e) => {
                    console.log('🔍 TeamNameModal: Form onSubmit event triggered');
                    handleSubmit(e);
                }}>
                    <div style={{
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'center',
                        gap: 'var(--space-5)'
                    }}>
                        {/* Team Name Field */}
                        <div style={{ width: '100%', maxWidth: '400px' }}>
                            <FormField label="Name Thy Team" required={false}>
                                <div style={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: 'var(--space-3)'
                                }}>
                                    <Input
                                        type="text"
                                        value={teamName}
                                        onChange={(e) => {
                                            console.log('🔍 TeamNameModal: Team name changed to:', e.target.value);
                                            handleTeamNameChange(e.target.value);
                                        }}
                                        placeholder="Enter team name"
                                        disabled={isSubmitting}
                                        // `Team.name` is max_length=100. Without this the field
                                        // accepted names the backend would truncate or reject.
                                        maxLength={TEAM_NAME_MAX_LENGTH}
                                        style={{ flex: 1, width: '250px' }}
                                    />
                                    <button
                                        type="button"
                                        onClick={generateRandomName}
                                        disabled={isSubmitting}
                                        style={{
                                            backgroundColor: 'transparent',
                                            border: 'none',
                                            cursor: isSubmitting ? 'not-allowed' : 'pointer',
                                            opacity: isSubmitting ? 0.6 : 1,
                                            padding: 'var(--space-2)',
                                            fontSize: '32px',
                                            lineHeight: '1',
                                            flexShrink: 0
                                        }}
                                        title="Generate New Name"
                                    >
                                        🎲
                                    </button>
                                </div>
                            </FormField>
                        </div>

                        {/* Profile Name Field — hidden when the target endpoint
                            cannot persist it (season create/join). Collecting a
                            value the server will discard is worse than not
                            offering it: the user picks a display name, the
                            preview promises it, and the team is created without
                            it. */}
                        {supportsKnownAs && (
                            <div style={{ width: '100%', maxWidth: '400px' }}>
                                <FormField label="Name Thyself" required={false}>
                                    <Input
                                        type="text"
                                        value={knownAs}
                                        onChange={(e) => {
                                            console.log('🔍 TeamNameModal: Known as changed to:', e.target.value);
                                            handleKnownAsChange(e.target.value);
                                        }}
                                        placeholder="Your profile name"
                                        disabled={isSubmitting}
                                        style={{ width: '250px' }}
                                    />
                                    <div style={{
                                        marginTop: 'var(--space-3)',
                                        display: 'flex',
                                        alignItems: 'center',
                                        gap: 'var(--space-2)',
                                        whiteSpace: 'nowrap'
                                    }}>
                                        <Checkbox
                                            id="hideKnownAs"
                                            checked={hideKnownAs}
                                            onChange={(e) => handleHideKnownAsChange(e.target.checked)}
                                            label="Conceal True Name"
                                            disabled={isSubmitting}
                                        />
                                    </div>
                                </FormField>
                            </div>
                        )}

                        {/* Wager and Win Info */}
                        <div style={{
                            textAlign: 'center',
                            width: '100%'
                        }}>
                            <div style={{
                                fontSize: '1rem',
                                color: 'var(--color-text-primary)',
                                marginBottom: 'var(--space-2)',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                gap: 'var(--space-2)'
                            }}>
                                <span>Wager:</span>
                                {/* The gem icon is hidden for a free entry — "💎 FREE"
                                    reads like a Mythril amount. */}
                                {leagueInfo?.entryFee !== 0 && (
                                    <span style={{ fontSize: '20px' }}>💎</span>
                                )}
                                <span style={{ fontWeight: 'bold' }}>
                                    {/*
                                      * THREE STATES, NEVER TWO — the same rule as SCORE/PROJ on
                                      * TeamPlayerCard:
                                      *
                                      *   a real fee   → the number      (e.g. "100")
                                      *   exactly 0    → "FREE"          (authoritatively no charge)
                                      *   absent/other → "—"             (we do not know)
                                      *
                                      * `0` and `unknown` are DIFFERENT ANSWERS and must not share a
                                      * rendering. A user confirming a season create/join is being
                                      * charged nothing — showing "—" there makes an authoritative
                                      * zero look like a price we failed to load, which is exactly
                                      * the ambiguity this modal exists to avoid.
                                      *
                                      * NEVER `entryFee || 100`. That was a money-facing bug on two
                                      * fronts: 0 is falsy, so a genuinely free league advertised a
                                      * 100 Mythril wager, and a caller that simply doesn't know the
                                      * fee yet (private creation has no fee data on hand) invented a
                                      * number the user was never charged.
                                      *
                                      * THE CALLER MUST HOLD UP ITS HALF. This guard only protects
                                      * against a fabricated fee if the CONTEXT BUILDER also declines
                                      * to invent one. Pass `entryFee: 0` ONLY when the endpoint
                                      * provably charges nothing; OMIT the key when the fee is merely
                                      * unloaded or unknown. Never use 0 to mean "we don't know" — it
                                      * now prints FREE. See
                                      * AIDocs/Final/frontend/component-reference-tdd.md.
                                      */}
                                    {leagueInfo?.entryFee === 0
                                        ? 'FREE'
                                        : typeof leagueInfo?.entryFee === 'number'
                                          ? leagueInfo.entryFee.toLocaleString()
                                          : '—'}
                                </span>
                            </div>
                            {/*
                              * PAYOUT ROW — suppressed entirely when the caller declares
                              * it has no payout truth (`leagueInfo.payoutKnown === false`).
                              *
                              * ⚠️ The 700 below is STILL A FABRICATED LITERAL on the
                              * weekly paths — no caller supplies it and no backend field
                              * feeds it. It is the same class of invented money-facing
                              * number as the old `entryFee || 100`, and it is left
                              * rendering there only because removing it from the weekly
                              * flow is a product decision, not a bug fix.
                              *
                              * WHY SEASON OPTS OUT RATHER THAN INHERITING IT: season
                              * rewards are per-rules-version `placement_rewards`, which
                              * are configurable per league and do not exist at all until
                              * the league is created. Quoting "700" to someone creating
                              * or joining a campaign advertises a payout that specific
                              * campaign may never pay. An honest omission beats a
                              * confident wrong number.
                              *
                              * When a real payout value lands, drive this from
                              * `leagueInfo.payout` using the same
                              * `typeof === 'number' ? value : '—'` rule as the wager
                              * above, and delete both this literal and the opt-out.
                              */}
                            {leagueInfo?.payoutKnown !== false && (
                                <div style={{
                                    fontSize: '1rem',
                                    color: 'var(--color-text-primary)',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    gap: 'var(--space-2)'
                                }}>
                                    <span>Win up to</span>
                                    <span style={{ fontSize: '20px' }}>💎</span>
                                    <span style={{ fontWeight: 'bold' }}>700</span>
                                </div>
                            )}
                        </div>

                        {/* Display Preview */}
                        <div style={{
                            backgroundColor: '#000000',
                            borderRadius: 'var(--radius-md)',
                            padding: 'var(--space-4)',
                            textAlign: 'center',
                            width: '100%'
                        }}>
                            <div style={{
                                fontSize: '0.75rem',
                                color: 'var(--color-text-tertiary)',
                                marginBottom: 'var(--space-2)'
                            }}>
                                Your team will appear as:
                            </div>
                            <div style={{
                                fontSize: '1.125rem',
                                fontWeight: 'bold',
                                color: '#63b3ed'
                            }}>
                                {getDisplayName()}
                            </div>
                        </div>

                        {/* Error Message */}
                        {error && (
                            <div style={{
                                backgroundColor: 'rgba(229, 62, 62, 0.1)',
                                border: '1px solid var(--color-error)',
                                borderRadius: 'var(--radius-md)',
                                padding: 'var(--space-3)',
                                width: '100%',
                                color: 'var(--color-error)',
                                fontSize: '0.875rem',
                                textAlign: 'center'
                            }}>
                                {error}
                            </div>
                        )}

                        {/* Action Buttons */}
                        <div style={{
                            display: 'flex',
                            gap: 'var(--space-3)',
                            justifyContent: 'flex-end',
                            width: '100%'
                        }}>
                            <Button
                                variant="secondary"
                                onClick={handleClose}
                                disabled={isSubmitting}
                                type="button"
                            >
                                Cancel
                            </Button>
                            <Button
                                variant="primary"
                                type="submit"
                                disabled={isSubmitting || !teamName.trim() || !knownAs.trim()}
                                loading={isSubmitting}
                                onClick={(e) => {
                                    console.log('🔍 TeamNameModal: Submit button clicked');
                                    console.log('🔍 TeamNameModal: Button disabled?', isSubmitting || !teamName.trim() || !knownAs.trim());
                                    console.log('🔍 TeamNameModal: Team name:', teamName.trim());
                                    console.log('🔍 TeamNameModal: Known as:', knownAs.trim());
                                }}
                            >
                                {isSubmitting ? 'Creating Team...' : 'Create Team & Join'}
                            </Button>
                        </div>

                        {/* Help Text */}
                        <div style={{
                            paddingTop: 'var(--space-5)',
                            borderTop: '1px solid var(--color-border-primary)',
                            fontSize: '0.75rem',
                            color: 'var(--color-text-tertiary)',
                            textAlign: 'center',
                            width: '100%'
                        }}>
                            Your preferences will be saved for future leagues.
                            You can edit your team name until the league starts.
                        </div>
                    </div>
                </form>
            )}
        </Modal>
    );
}

export default TeamNameModal;
